"use client";

import { useEffect, useState } from "react";
import { Button, TextInput } from "@mantine/core";
import { showToast } from "../../../components/custom/custom-toast";
import { PrivilegeService } from "../services/privilege-service";
import { PrivilegeFunctionGroup as PrivilegeFunctionGroupType } from "../types/privilege-types";
import PrivilegeFunctionGroup from "../components/PrivilegeFunctionGroup";

interface Props {
  groupId?: number; // nếu có => chế độ update
  onClose: () => void;
  onSuccess?: () => void;
}

export default function PrivilegeGroupDetailModal({ groupId, onClose, onSuccess }: Props) {
  const [name, setName] = useState("");
  const [privilegeGroups, setPrivilegeGroups] = useState<PrivilegeFunctionGroupType[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);

  const { fetch: getPrivileges } = PrivilegeService.listPrivileges();
  const { fetch: getGroupDetail } = PrivilegeService.detailGroup();
  const { fetch: createGroup } = PrivilegeService.createGroup();
  const { fetch: updateGroup } = PrivilegeService.updateGroup();

  // 🟢 Load danh sách quyền
  useEffect(() => {
    (async () => {
      const res = await getPrivileges();
      if (res?.data) setPrivilegeGroups(res.data);
    })();
  }, []);

  // 🟠 Nếu là edit → load chi tiết nhóm
  useEffect(() => {
    if (!groupId) return;
    (async () => {
      const res = await getGroupDetail(groupId);
      if (res?.data) {
        setName(res.data.name);
        setSelectedIds(res.data.privilege_ids || []);
      }
    })();
  }, [groupId]);

  // 🧩 Toggle chọn quyền
  const handleToggle = (id: number, checked: boolean) => {
    setSelectedIds((prev) => (checked ? [...prev, id] : prev.filter((x) => x !== id)));
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      showToast({ message: "Vui lòng nhập tên nhóm quyền", type: "error" });
      return;
    }
    if (selectedIds.length === 0) {
      showToast({ message: "Danh sách quyền không được để trống", type: "error" });
      return;
    }

    const payload = { name, privilege_ids: selectedIds };
    setLoading(true);

    const res = groupId ? await updateGroup(groupId, payload) : await createGroup(payload);

    setLoading(false);

    if (res?.status === 200) {
      showToast({
        message: groupId ? "Cập nhật nhóm quyền thành công" : "Tạo nhóm quyền thành công",
        type: "success",
      });
      onSuccess?.();
      onClose();
    } else {
      showToast({ message: "Lưu nhóm quyền thất bại", type: "error" });
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50">
      <div className="bg-white rounded-2xl p-6 w-[800px] max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-bold mb-4">
          {groupId ? "Cập nhật nhóm quyền" : "Tạo nhóm quyền"}
        </h2>

        {/* Nhập tên nhóm quyền */}
        <TextInput
          label="Tên nhóm quyền"
          placeholder="Nhập tên nhóm quyền"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          className="mb-6"
        />

        {/* Danh sách quyền */}
        <div className="space-y-4">
          {privilegeGroups.map((group) => (
            <PrivilegeFunctionGroup
              key={group.id}
              group={group}
              selectedIds={selectedIds}
              onToggle={handleToggle}
            />
          ))}
        </div>

        {/* Action */}
        <div className="flex justify-end space-x-3 mt-6">
          <Button variant="outline" onClick={onClose}>
            Hủy
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? "Đang lưu..." : "Lưu nhóm quyền"}
          </Button>
        </div>
      </div>
    </div>
  );
}
